import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';

import { Input } from '@/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { showToast, ToastType } from '@/helpers/toast';
import { useGetCurrentUserQuery, useUpdatePersonalInformationMutation } from '@/services';

import { personalDetailsValidationSchema } from './PersonalDetailsValidation';
import {
  PERSONAL_DETAILS_FORM_CONFIGURATION,
  PersonalDetailsType
} from './profile-page-configuration';

export const PersonalDetails = () => {
  const { data: user, isLoading } = useGetCurrentUserQuery(true);
  const [t] = useTranslation('common');
  const queryClient = useQueryClient();

  const isEmailChangePending = useRef(false);
  const valuesAfterSave = useRef<PersonalDetailsType | null>(null);
  const formikHelpersRef = useRef<FormikHelpers<PersonalDetailsType> | null>(null);

  const { mutate: updatePersonalInformation, isLoading: isUpdating } =
    useUpdatePersonalInformationMutation(queryClient, {
      onSuccess: () => {
        showToast(
          ToastType.SUCCESS,
          isEmailChangePending.current ? t('emailChangeConfirmationSent') : t('userUpdated')
        );

        if (valuesAfterSave.current) {
          formikHelpersRef.current?.resetForm({ values: valuesAfterSave.current });
        }
      },
      onError: (error) => {
        showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
      }
    });

  const getInitVales = () => {
    return {
      firstName: user?.data.firstName || '',
      lastName: user?.data.lastName || '',
      email: user?.data.email || ''
    };
  };

  const handleSubmit = (values: PersonalDetailsType, formikHelpers: FormikHelpers<PersonalDetailsType>) => {
    isEmailChangePending.current = user?.data.email !== values.email;
    // The email only becomes effective once the confirmation link is used, so the field
    // should keep showing the current (unchanged) email until then.
    valuesAfterSave.current = {
      ...values,
      email: isEmailChangePending.current ? user?.data.email || '' : values.email
    };
    formikHelpersRef.current = formikHelpers;

    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email
    };

    updatePersonalInformation(payload);
  };

  return (
    <div>
      <Label
        text={t('personalInformation')}
        type={LABEL_TYPE.H3}
        className='mb-3 text-primary-blue'
      />
      <Formik
        enableReinitialize
        initialValues={getInitVales()}
        onSubmit={handleSubmit}
        validationSchema={personalDetailsValidationSchema}>
        {({ errors, touched, isValid, dirty }) => (
          <Form className='flex w-full flex-col'>
            <div className='space-y-5'>
              {PERSONAL_DETAILS_FORM_CONFIGURATION.map((fieldConfig, index) => (
                <Input
                  key={index}
                  id={fieldConfig.id}
                  label={t(fieldConfig.label)}
                  type={fieldConfig.type}
                  errorMessage={errors[fieldConfig.id as keyof PersonalDetailsType] as string}
                  isInvalid={
                    !!errors[fieldConfig.id as keyof PersonalDetailsType] &&
                    !!touched[fieldConfig.id as keyof PersonalDetailsType]
                  }
                />
              ))}
            </div>
            <div className='flex justify-end'>
              <Button
                type='submit'
                className='mt-5'
                variant={ButtonVariant.PRIMARY}
                disabled={isUpdating || isLoading || !(isValid && dirty)}>
                {t('saveChanges')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
